import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button, Typography, Input, Grid, message, Empty, Pagination, Space, Flex, Card } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { deleteKnowledgeBase, getknowledgeDatabases } from './KnowledgeDatabaseServices';
import { debounce } from 'lodash';
import AddKnowledgeBaseModal from './AddKnowledgeBaseModal/AddKnowledgeBaseModal.js';
import DetailModal from './DetailModal/DetailModal.js';
import KnowledgeCard from './KnowledgeCard/KnowledgeCard.js';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext.js';
import KnowledgeBaseSkeleton from './KnowledgeBaseSkeleton';
import useSocket from '../../Hooks/useSocket';
import useSocketSubscription from '../../Hooks/useSocketSubscription';
import useSocketEvent from '../../Hooks/useSocketEvent';
import { Resource, Action } from '../../Constants/socket';
import { transformKnowledgeBaseData } from './utils/transformations';

const { Title } = Typography;
const { useBreakpoint } = Grid;

const KnowledgeDatabasePage = () => {
    const { t } = useTranslation();
    const { setLoading } = useGlobal();
    const [skeleton, setSkeleton] = useState(true);
    const [expandedKB, setExpandedKB] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [knowledgeBase, setKnowledgeBase] = useState([]);
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const navigate = useNavigate();
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 25,
        total: 0
    });
    const screens = useBreakpoint();
    const { connectionState } = useSocket();
    const isSubscribed = useSocketSubscription(Resource.KNOWLEDGE_BASE);
    const knowledgeBaseIds = useMemo(() => knowledgeBase.map(kb => kb.id), [knowledgeBase]);
    useSocketSubscription(Resource.KNOWLEDGE_BASE, knowledgeBaseIds);

    const debouncedFetch = useRef(
        debounce((page, size, search) => {
            const fetchData = async () => {
                try {
                    setLoading(true);
                    const { data, success, metadata } = await getknowledgeDatabases({
                        page,
                        size,
                        search
                    });

                    if (success) {
                        const transformedData = data.map(transformKnowledgeBaseData);
                        setKnowledgeBase(transformedData);
                        setPagination({
                            current: metadata.page,
                            pageSize: metadata.per_page,
                            total: metadata.total
                        });
                    } else {
                        message.error({ content: data.message || t('failed_to_fetch_knowledge_bases'), key: 'loading' });
                    }
                } catch (error) {
                    message.error({
                        content: error.response?.data?.message || t('error_fetching_knowledge_bases'),
                        key: 'loading'
                    });
                } finally {
                    setLoading(false);
                    setSkeleton(false);
                }
            };
            fetchData();
        }, 500)
    ).current;

    useEffect(() => {
        return () => {
            debouncedFetch.cancel();
        };
    }, []);

    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchText(value);
        setPagination(prev => ({ ...prev, current: 1 }));
        debouncedFetch(1, pagination.pageSize, value);
    };

    useEffect(() => {
        debouncedFetch(pagination.current, pagination.pageSize, searchText);
    }, [pagination.current, pagination.pageSize]);

    const handlePageChange = (page, pageSize) => {
        setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize
        }));
    };

    const handleDelete = useCallback(async (id) => {
        try {
            setLoading(true);
            const { success, data } = await deleteKnowledgeBase(id);

            if (success) {
                debouncedFetch(pagination.current, pagination.pageSize, searchText);
            } else {
                message.error({ content: data.message || t('failed_to_delete_knowledge_base'), key: 'deleting' });
            }
        } catch (error) {
            message.error({
                content: t('error_deleting_knowledge_base'),
                key: 'deleting'
            });
        } finally {
            setLoading(false);
        }
    }, [pagination, searchText, debouncedFetch, setLoading, t]);

    // Handle new knowledge base
    const handleKnowledgeBaseCreated = useCallback((data) => {
        debouncedFetch(pagination.current, pagination.pageSize, searchText);
    }, [pagination.current, pagination.pageSize, searchText, debouncedFetch]);

    // Handle updated knowledge base
    const handleKnowledgeBaseUpdated = useCallback((data) => {
        setKnowledgeBase(prev => 
            prev.map(kb => kb.id === data.id ? transformKnowledgeBaseData(data) : kb)
        );
    }, []);

    // Handle deleted knowledge base
    const handleKnowledgeBaseDeleted = useCallback((data) => {
        debouncedFetch(pagination.current, pagination.pageSize, searchText);
    }, [pagination.current, pagination.pageSize, searchText, debouncedFetch]);

    // Register socket event listeners
    useSocketEvent(Resource.KNOWLEDGE_BASE, Action.CREATE, handleKnowledgeBaseCreated);
    useSocketEvent(Resource.KNOWLEDGE_BASE, Action.UPDATE, handleKnowledgeBaseUpdated);
    useSocketEvent(Resource.KNOWLEDGE_BASE, Action.DELETE, handleKnowledgeBaseDeleted);

    if (!connectionState.status === 'connected') {
        return <div>Socket disconnected. Trying to reconnect...</div>;
    }

    return (
        <Flex style={{ padding: 0, flexGrow: 1 }} gap={16} vertical>
            <Card
                bordered={false}
                title={t('knowledge_base')} extra={<Button
                    color="primary"
                    icon={<PlusOutlined />}
                    variant="link"
                    onClick={() => setIsAddModalVisible(true)}
                >
                    {t('add_knowledge_base')}
                </Button>}
                styles={{
                    body: {
                        padding: 16
                    }
                }}
            >
                <Flex
                    gap="middle"
                    wrap="wrap"
                >
                    <Flex
                        flex={{ xs: '100%', md: 1 }}
                        style={{ flexGrow: 1 }}
                    >
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder={t('search_knowledge_base')}
                            size="medium"
                            onChange={handleSearchChange}
                            onFocus={() => setIsInputFocused(true)}
                            onBlur={() => setIsInputFocused(false)}
                            style={{
                                width: isInputFocused ? '100%' : (screens.xs ? '100%' : 400),
                                transition: 'width 0.4s ease-in-out'
                            }}
                        />
                    </Flex>
                </Flex>
            </Card>

            {skeleton ? (
                <KnowledgeBaseSkeleton />
            ) : (
                knowledgeBase.length === 0 ? (
                    <Empty
                        style={{ flexGrow: 1 }}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <Space direction="vertical" align="center">
                                <Typography.Title level={5}>{t('no_knowledge_bases_found')}</Typography.Title>
                                <Typography.Text type="secondary">
                                    {t('click_add_agent_to_create_your_first_one')}
                                </Typography.Text>
                            </Space>
                        }
                    />
                ) : (
                    <Flex gap={16} wrap justify="center" style={{ flexGrow: 1, alignItems: 'stretch' }}>
                        {knowledgeBase.map(item => (
                            <KnowledgeCard
                                key={item.id}
                                item={item}
                                onDelete={handleDelete}
                                onExpand={() => setExpandedKB(item)}
                            />
                        ))}
                    </Flex>
                )
            )}
            <Flex justify="center">
                <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={handlePageChange}
                    showSizeChanger
                    pageSizeOptions={[10, 25, 50, 100]}
                    showTotal={(total, range) =>
                        `${range[0]}-${range[1]} ${t('of')} ${total} ${t('items')}`
                    }
                />
            </Flex>

            <AddKnowledgeBaseModal
                visible={isAddModalVisible}
                onClose={() => setIsAddModalVisible(false)}
                navigate={navigate}
            />

            <DetailModal
                item={expandedKB}
                onClose={() => setExpandedKB(null)}
            />
        </Flex>
    );
};

export default KnowledgeDatabasePage;
